import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getSelectedFilter } from 'features/BoxSizeFilter';
import { getSelectedSizeCodeId } from 'pages/BookingPage';
import { SizeCodeForBooking, SizeCodeType } from 'entities/SizeCode';
import { RentOptionDocument } from 'entities/RentOption';
import { Box } from 'entities/Box';
import { NoResults } from 'shared/ui/NoResults';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { SizeCodeCard } from './SizeCodeCard';
import { SizeCodeCardMobile } from './SizeCodeCardMobile';
import { BoxSelectionModal } from './BoxSelectionModal';
import { BoxSelectionModalMobile } from './BoxSelectionModalMobile';

interface ModernSizeCodeSelectProps {
  sizeCodes: SizeCodeForBooking[] | undefined;
  rentOptions: RentOptionDocument[] | undefined;
  selectedBoxId: string | undefined;
  onSelectBox: (box: Box, sizeCodeId: string) => void;
  onBookBox: (box: Box, sizeCodeId: string) => void;
}

export const ModernSizeCodeSelect: FC<ModernSizeCodeSelectProps> = memo((props) => {
  const { sizeCodes = [], rentOptions = [], selectedBoxId, onSelectBox, onBookBox } = props;

  const { t } = useAppTranslation('booking');
  const { isDesktop } = useScreenBreakpoints();

  const [selectedSizeCodeForModal, setSelectedSizeCodeForModal] = useState<SizeCodeForBooking | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const boxSizeFilter = useAppSelector(getSelectedFilter);
  const selectedSizeCodeId = useAppSelector(getSelectedSizeCodeId);

  const filterBySizeCode = useMemo<SizeCodeForBooking[]>(() => {
    return sizeCodes.filter((sizeCode) => (boxSizeFilter === 'All' ? true : boxSizeFilter === sizeCode.sizeGroup));
  }, [boxSizeFilter, sizeCodes]);

  const availableSizeCodes = useMemo(() => {
    return filterBySizeCode
      .filter((sizeCode) => sizeCode.availableBoxesCount > 0)
      .sort((a, b) => {
        if (a.sizeCodeType === SizeCodeType.CUBIC_BOX) return -1;
        if (b.sizeCodeType === SizeCodeType.CUBIC_BOX) return 1;
        return 0;
      });
  }, [filterBySizeCode]);

  const handleSizeCodeClick = useCallback((sizeCode: SizeCodeForBooking) => {
    setSelectedSizeCodeForModal(sizeCode);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedSizeCodeForModal(null);
  }, []);

  const handleSelectBox = useCallback(
    (box: Box) => {
      if (selectedSizeCodeForModal) {
        onSelectBox(box, selectedSizeCodeForModal.sizeCodeId);
      }
    },
    [selectedSizeCodeForModal, onSelectBox],
  );

  const handleBookBox = useCallback(
    (box: Box) => {
      if (selectedSizeCodeForModal) {
        onBookBox(box, selectedSizeCodeForModal.sizeCodeId);
        handleCloseModal();
      }
    },
    [selectedSizeCodeForModal, onBookBox, handleCloseModal],
  );

  if (!filterBySizeCode.length) {
    return <NoResults title={t('No available size codes')} />;
  }

  if (!availableSizeCodes.length) {
    return <NoResults title={t('No available size codes')} />;
  }

  return (
    <>
      <div className="space-y-4">
        {availableSizeCodes.map((sizeCode) => {
          const isSelected = selectedSizeCodeId === sizeCode.sizeCodeId;

          if (isDesktop) {
            return (
              <SizeCodeCard
                key={sizeCode.sizeCodeId}
                sizeCode={sizeCode}
                rentOptions={rentOptions}
                isSelected={isSelected}
                onClick={() => {
                  handleSizeCodeClick(sizeCode);
                }}
              />
            );
          }

          return (
            <SizeCodeCardMobile
              key={sizeCode.sizeCodeId}
              sizeCode={sizeCode}
              isSelected={isSelected}
              onClick={() => {
                handleSizeCodeClick(sizeCode);
              }}
            />
          );
        })}
      </div>

      {selectedSizeCodeForModal && isDesktop && (
        <BoxSelectionModal
          isOpen={isModalOpen}
          sizeCode={selectedSizeCodeForModal}
          rentOptions={rentOptions}
          selectedBoxId={selectedBoxId}
          onClose={handleCloseModal}
          onSelectBox={handleSelectBox}
          onBookBox={handleBookBox}
        />
      )}

      {selectedSizeCodeForModal && !isDesktop && (
        <BoxSelectionModalMobile
          isOpen={isModalOpen}
          sizeCode={selectedSizeCodeForModal}
          rentOptions={rentOptions}
          selectedBoxId={selectedBoxId}
          onClose={handleCloseModal}
          onSelectBox={handleSelectBox}
          onBookBox={handleBookBox}
        />
      )}
    </>
  );
});
